<template lang="pug">
v-expansion-panels.suus__event-form__expansion-panels(
    accordion
    flat
    tile
    multiple
    v-model="activePanelsIndizes"
)
    v-expansion-panel(v-for="(item, i) in panels" :key="i")
        v-expansion-panel-header(:id="item.selector")
            Header(:text="item.title" :level="5" customClass="mb-0")
            template(v-slot:actions)
                v-icon(color="textColor") {{ panelIcon }}
        v-expansion-panel-content().mt-6
            component(:is="item.component" @validate="validateForm")
</template>
<script lang="ts">
import Header from "../../../components/UI/Header.vue"
import { GeneralFieldsDefinition } from "features/events/form/general-fields/GeneralFields.vue"
import { ReminderFieldsDefinition } from "components/EventForm/ReminderFields.vue"
import { DangerZoneFieldsDefinition } from "components/EventForm/DangerZoneFields.vue"
import { OrganizationFieldsDefinition } from "features/events/form/organization-fields/OrganizationFields.vue"
import { mdiChevronDown } from "@mdi/js"
import {
  Component,
  computed,
  ComputedRef,
  defineComponent,
  Ref,
  ref,
  WritableComputedRef,
} from "vue"
import useTranslate from "features/common/translate/concerns/useTranslate"
import { PanelIdentifier } from "features/events/form/event-form.types"

export type PanelDefinition = {
  key: string
  component: Component
  id: PanelIdentifier
}

type Panel = {
  title: string
  selector: string
  component: Component
  id: PanelIdentifier
}

export default defineComponent({
  name: "FormExpansionPanel",
  components: {
    Header,
  },
  props: {
    hasDangerZone: { type: Boolean, default: false },
    allowedToPublish: { type: Boolean, default: false },
  },
  data() {
    return {
      panelIcon: mdiChevronDown,
    }
  },
  methods: {
    addActivePanel(panelId: PanelIdentifier) {
      if (!this.activePanels.includes(panelId)) {
        this.activePanels.push(panelId)
      }
    },
    validateForm() {
      this.$emit("validate")
    },
  },
  setup(props) {
    const { t$ } = useTranslate("components.event_form")

    const panels: ComputedRef<Panel[]> = computed(() => {
      const panelDefinitions: PanelDefinition[] = [
        GeneralFieldsDefinition,
        OrganizationFieldsDefinition,
        ReminderFieldsDefinition,
        ...(props.hasDangerZone ? [DangerZoneFieldsDefinition] : []),
      ]

      return panelDefinitions.map((panelDefinition) => ({
        title: t$("." + panelDefinition.key),
        selector: `suus__event-form__${panelDefinition.key}`,
        component: panelDefinition.component,
        id: panelDefinition.id,
      }))
    })

    const activePanels: Ref<PanelIdentifier[]> = ref([
      PanelIdentifier.GENERAL,
      PanelIdentifier.ORGANIZATION,
      PanelIdentifier.REMINDER,
    ])

    const activePanelsIndizes: WritableComputedRef<number[]> = computed({
      get() {
        return activePanels.value
          .filter((panelId) => {
            return panels.value.some((panel) => panel.id === panelId)
          })
          .map((panelId) =>
            panels.value.findIndex((panel) => panel.id === panelId)
          )
      },
      set(value: number[]) {
        activePanels.value = value.map((index) => panels.value[index].id)
      },
    })

    return {
      panels,
      activePanels,
      activePanelsIndizes,
    }
  },
})
</script>

<style lang="sass" scoped>
.suus__event-form__expansion-panels::v-deep
    .v-expansion-panel
        background-color: transparent !important
    .v-expansion-panel-header
        border-top: 1px solid #DADADA
        padding-right: 0
        padding-left: 0
    .v-expansion-panel-content
        .v-expansion-panel-content__wrap
            padding-right: 0 !important
            padding-left: 0 !important
</style>
